import React, { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    IconButton,
    TextField,
    Typography,
    Box,
    styled,
} from '@mui/material';
import {
    Cancel,
    Save,
    Edit,
    Delete,
    Close,
    FilterAlt,
    Add
}
from '@mui/icons-material'

import StyledTableCell from '../../styled/TableCell';
import StyledTableRow from '../../styled/TableRow';

import store from "../../../store/index";
import { toggleMessageOpen } from "../../../store/actions/Message";
import apiClient from '../../../services/api';
import { LoadingScreen } from '../../partials/LoadingScreen';
import StyledTextField from '../../styled/TextField';
import StyledPaper from '../../styled/Paper';
import Flex, { FlexGrow } from '../../styled/FlexBox';

const PaperStack = styled(StyledPaper)(
    ({ theme }) => ({
        padding: theme.spacing(2),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
    }),
);

const Actions = ({item, setCustomer, setDialogOpen, handleDelete, t}) => {
    return (
        <Fragment>
            <IconButton onClick={() => { setCustomer(item); setDialogOpen(true) }}>
                <Edit color="primary" />
            </IconButton>
            <IconButton onClick={() => { if(window.confirm(t('Are you sure?'))) handleDelete(item) }}>
                <Delete color="primary" />
            </IconButton>
        </Fragment>
    )
}

const CustomerTable = ({data, columns, setData, setCustomer, setDialogOpen, handleDelete, t}) => {
    const [orderBy, setOrderBy] = useState(null);
    const [direction, setDirection] = useState('asc');
    const [filterColumn, setFilterColumn] = useState(null);
    const [filterValue, setFilterValue] = useState('');

    const [sortedFiltered, setSortedFiltered] = useState(data || []);

    useEffect(() => {
        const filtered = filterColumn && filterValue
            ? data.filter(
                (r) => r[filterColumn].toLowerCase().indexOf(filterValue.toLowerCase()) >= 0
            ) : data.map(r => r);
        const sorted = filtered;
        const dir = direction === 'asc' ? 1 : -1;

        sorted.sort((a, b) => {
            const va = a[orderBy];
            const vb = b[orderBy];
            
            if(va === vb) {
                return 0;
            }

            if(typeof va === 'string') {
                return va.localeCompare(vb) * dir;
            }

            return (va > vb ? 1 : -1) * dir;
        });

        setSortedFiltered(sorted);
    }, [orderBy, direction, filterColumn, filterValue, data]);

    const handleSort = (id) => {
        if(id === orderBy) {
            if(direction === 'asc') {
                setDirection('desc')
            } else {
                setDirection('asc');
            }
        } else {
            setDirection('asc');
            setOrderBy(id);
        }
    }

    return (
        <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
            <Table stickyHeader aria-label="projects table" size='small'>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell
                                key={c.id}
                                /*sx={{ display: 'flex', justifyContent: '' }}*/
                                align={c.numeric ? 'right' : 'left'}
                                padding="none"
                            >
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => {
                                        setFilterColumn(filterColumn === c.id ? null : c.id);
                                        setFilterValue('');
                                    }}
                                >
                                    {filterColumn === c.id ? <Close fontSize="small" /> : <FilterAlt fontSize="small" />}
                                </IconButton>
                                {filterColumn === c.id ? (
                                    <TextField
                                        autoFocus
                                        size="small"
                                        value={filterValue}
                                        onChange={(e) => setFilterValue(e.target.value)}
                                        placeholder={t(c.header)}
                                        variant="standard"
                                        color="secondary"
                                    />
                                ) : (
                                    <TableSortLabel
                                        direction={direction}
                                        active={orderBy === c.id}
                                        onClick={() => handleSort(c.id)}
                                    >
                                        {c.header}
                                    </TableSortLabel>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedFiltered.map((item, index) => {
                        return (
                            <StyledTableRow key={index}>
                                {columns.map((column) => (
                                    column.id !== 'actions'
                                        ?   <StyledTableCell>{item[column.id]}</StyledTableCell>
                                        :   <Actions
                                                item={item}
                                                setCustomer={setCustomer}
                                                setDialogOpen={setDialogOpen}
                                                handleDelete={handleDelete}
                                                t={t}
                                            />
                                ))}
                            </StyledTableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function Customers() {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const isLoggedIn = useSelector((state) => state.auth.loggedIn);

    const [data, setData] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [id, setId] = useState(null);
    const [number, setNumber] = useState('');
    const [name1, setName1] = useState('');
    const [name2, setName2] = useState('');
    const [name3, setName3] = useState('');
    const [street, setStreet] = useState('');
    const [zip, setZip] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'customers'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps
    
  
    useEffect(() => { 
        apiClient.get('/api/customer').then(response => { 
            setData(response.data);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t("Customers cant't be loaded"), "error"));
        })
    }, [isLoggedIn, t]);

    const columns = [
        { id: 'number', header: t('Number')},
        { id: 'name1', header: t('Name')+'1'},
        { id: 'name2', header: t('Name')+'2'},
        { id: 'name3', header: t('Name')+'3'},
        { id: 'street', header: t('Street')},
        { id: 'zip', header: t('Zip')},
        { id: 'city', header: t('City')},
        { id: 'country', header: t('Country')},
        { id: 'email', header: t('Email')},
        { id: 'phone', header: t('Phone')},
        { id: 'actions', header: t('Actions')}
    ];
  
    const handleCloseDialog = () => { 
        setDialogOpen(false);
        clearState(true);
        setErrors([]);
    }

    const setCustomer = (customer) => {
        setId(customer.id);
        setNumber(customer.number);
        setName1(customer.name1);
        setName2(customer.name2);
        setName3(customer.name3);
        setStreet(customer.street);
        setZip(customer.zip);
        setCity(customer.city);
        setCountry(customer.country);
        setEmail(customer.email);
        setPhone(customer.phone);
    }

    const clearState = () => {
        setId(null);
        setNumber('');
        setName1('');
        setName2('');
        setName3('');
        setStreet('');
        setZip('');
        setCity('');
        setCountry('');
        setEmail('');
        setPhone('');
    }

    const handleChangeNumber = (event) => { setNumber( event.target.value ) }
    const handleChangeName1 = (event) => { setName1( event.target.value ) }
    const handleChangeName2 = (event) => { setName2( event.target.value ) }
    const handleChangeName3 = (event) => { setName3( event.target.value ) }
    const handleChangeStreet = (event) => { setStreet( event.target.value ) }
    const handleChangeZip = (event) => { setZip( event.target.value ) }
    const handleChangeCity = (event) => { setCity( event.target.value ) }
    const handleChangeCountry = (event) => { setCountry( event.target.value ) }
    const handleChangeEmail = (event) => { setEmail( event.target.value ) }
    const handleChangePhone = (event) => { setPhone( event.target.value ) }

    const handleUpdate = () => {
        apiClient.put(
            '/api/customer/update',
            {
                id: id,
                number: number,
                name1: name1,
                name2: name2,
                name3: name3,
                street: street,
                zip: zip,
                city: city,
                country: country,
                email: email,
                phone: phone
            }
        ).then(response => {
            handleCloseDialog();
            setErrors([]);
            setData(response.data);
            store.dispatch(toggleMessageOpen(t('Customer saved successfully'), "success"));
        }).catch( error => setErrors(error.response.data) );
    }

    const handleAdd = () => {
        apiClient.post(
            '/api/customer',
            {
                number: number,
                name1: name1,
                name2: name2,
                name3: name3,
                street: street,
                zip: zip,
                city: city,
                country: country,
                email: email,
                phone: phone
            }
        ).then(response => {
            handleCloseDialog();
            setErrors([]);
            setData(response.data);
            store.dispatch(toggleMessageOpen(t('Customer saved successfully'), "success"));
        }).catch( error => setErrors(error.response.data) );
    }

    const handleDelete = (oldData) => {
        apiClient.delete('/api/customer/'+oldData.id).then( response => {
            setData(response.data);
            store.dispatch(toggleMessageOpen(t('Customer deleted successfully'), "success"));
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")) );
    }

    const renderData = () => {
        return (
            <Box height="100%" padding={3} overflow="hidden">
                <Helmet>
                    <title>{t("Customers")}</title>
                </Helmet>
                <PaperStack>
                    <Flex justifyContent="end" pb={2}>
                        <Button
                            size='small'
                            variant='contained'
                            startIcon={<Add />}
                            onClick={() => {setDialogOpen(true)}}
                        >
                            {t('Add new')}
                        </Button>
                    </Flex>
                    <FlexGrow>
                        <CustomerTable
                            columns={columns}
                            data={data}
                            setCustomer={setCustomer}
                            setDialogOpen={setDialogOpen}
                            setData={setData}
                            handleDelete={handleDelete}
                            t={t}
                        />
                    </FlexGrow>
                </PaperStack>
                <Dialog open={dialogOpen}>
                    <DialogTitle>
                        <Typography variant="h2">
                            {id !== null ? t('Update') : t('Add new') }
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <StyledTextField
                            margin="dense"
                            id="number"
                            label={t("Number")}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={number}
                            onChange={handleChangeNumber}
                            error={!!errors.number}
                            helperText={errors.number && errors.number}
                        />
                        <StyledTextField
                            margin="dense"
                            id="name1"
                            label={t("Name")+'1'}
                            type="name1"
                            fullWidth
                            variant="outlined"
                            value={name1}
                            onChange={handleChangeName1}
                            error={!!errors.name1}
                            helperText={errors.name1 && errors.name1}
                        />
                        <StyledTextField
                            margin="dense"
                            id="name2"
                            label={t("Name")+'2'}
                            type="name2"
                            fullWidth
                            variant="outlined"
                            value={name2}
                            onChange={handleChangeName2}
                            error={!!errors.name2}
                            helperText={errors.name2 && errors.name2}
                        />
                        <StyledTextField
                            margin="dense"
                            id="name3"
                            label={t("Name")+'3'}
                            type="name3"
                            fullWidth
                            variant="outlined"
                            value={name3}
                            onChange={handleChangeName3}
                            error={!!errors.name3}
                            helperText={errors.name3 && errors.name3}
                        />
                        <StyledTextField
                            margin="dense"
                            id="street"
                            label={t("Street")}
                            type="street"
                            fullWidth
                            variant="outlined"
                            value={street}
                            onChange={handleChangeStreet}
                            error={!!errors.street}
                            helperText={errors.street && errors.street}
                        />
                        <StyledTextField
                            margin="dense"
                            id="zip"
                            label={t("Zip")}
                            type="zip"
                            fullWidth
                            variant="outlined"
                            value={zip}
                            onChange={handleChangeZip}
                            error={!!errors.zip}
                            helperText={errors.zip && errors.zip}
                        />
                        <StyledTextField
                            margin="dense"
                            id="city"
                            label={t("City")}
                            type="city"
                            fullWidth
                            variant="outlined"
                            value={city}
                            onChange={handleChangeCity}
                            error={!!errors.city}
                            helperText={errors.city && errors.city}
                        />
                        <StyledTextField
                            margin="dense"
                            id="country"
                            label={t("Country")}
                            type="country"
                            fullWidth
                            variant="outlined"
                            value={country}
                            onChange={handleChangeCountry}
                            error={!!errors.country}
                            helperText={errors.country && errors.country}
                        />
                        <StyledTextField
                            margin="dense"
                            id="email"
                            label={t("Email")}
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={email}
                            onChange={handleChangeEmail}
                            error={!!errors.email}
                            helperText={errors.email && errors.email}
                        />
                        <StyledTextField
                            margin="dense"
                            id="phone"
                            label={t("Phone")}
                            type="phone"
                            fullWidth
                            variant="outlined"
                            value={phone}
                            onChange={handleChangePhone}
                            error={!!errors.phone}
                            helperText={errors.phone && errors.phone}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseDialog}
                            startIcon={<Cancel/>}
                            variant="contained"
                            size='small'
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            onClick={( id !== null ? handleUpdate : handleAdd )}
                            startIcon={<Save/>}
                            variant="contained"
                            size='small'
                        >
                            {t('Save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    )
}

export default Customers;
